<script setup lang="ts">
import { i18n } from '@/i18n';
import { useUpdateBaseBusinessForm } from '@/corePagesModule/updateBusinessModal/composables/useUpdateBaseBusinessForm';
import AddOptionWideSection from './AddOptionWideSection.vue';
import { formOptionsBusiness, inputTypeKeyMap, keyMappingForSelectInput } from '@/corePagesModule/updateBusinessModal/constants';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { ref } from 'vue';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { BaseBusinessNullableFormState, BASE_BUSINESS_NULLABLE_FORM_DEFAULT_STATE, BaseBusinessNullableFormItem } from '../constants';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import TableCheckbox from './TableCheckbox.vue';
import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { SettingRepository } from '@/coreInfrastructureModule/Repositories/SettingRepository';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import MultiTagInput from '@/corePagesModule/components/MultiTagInput.vue';

const t = i18n.t;

const {
  fieldState,
  allValidate,
  isEdited,
  addNewOption,
  updateValue,
  updateUserValue,
  createBaseBusinessPatch,
  currentOpenSelectInputName,
  initializeForm,
} = useUpdateBaseBusinessForm({
  validateRuleKeyMap: {
    customerNumber: [],
    customerName: [],
    salesDestinationNumber: [],
    salesDestinationName: [],
    endUser: [],
    baseBusinessResponsibleUser: [],
  },
});

const selectedUserAccounts = ref<UserAccount[]>([]);
const nullableFormState = ref<BaseBusinessNullableFormState>({ ...BASE_BUSINESS_NULLABLE_FORM_DEFAULT_STATE });
const businessFreeColumnValues = ref<BusinessFreeColumnValue[]>([]);
const editedBusinessFreeColumnNos = ref(new Set<number>());
const settingRepository = new SettingRepository();

/**
 * ユーザー情報をuserAccountsのまま変更する
 * @param userAccounts - 変更後ユーザー情報
 */
const updateUserAccountsValue = (userAccounts: UserAccount[]) => {
  selectedUserAccounts.value = userAccounts;
};

/**
 * NULLにするチェックボックスのハンドラ
 * @param option - ヘッダー名
 * @param isChecked - チェックの有無
 */
const isChangedNullableCheckbox = (option: string, isChecked: boolean) => {
  nullableFormState.value[option] = isChecked;
  updateValue(option, '');
};

/** 自由欄設定を取得して一括編集フォームに反映する */
const initializeBusinessFreeColumns = async () => {
  const result = await settingRepository.getBusinessFreeColumnSetting();
  if (result.status.type !== 'success') {
    businessFreeColumnValues.value = [];
    return;
  }
  businessFreeColumnValues.value = result.status.data.base
    .map((item, index) => ({
      columnNo: index + 1,
      headerName: item.headerName,
      isMulti: item.isMulti,
      isConfigured: item.isConfigured ?? false,
      value: '',
      target: 'base' as const,
    }))
    .filter((item) => item.isConfigured);
};

/**
 * 自由欄の入力値を更新する
 * @param index - 自由欄配列のindex
 * @param value - 入力値
 */
const updateBusinessFreeColumnValue = (index: number, value: string) => {
  const target = businessFreeColumnValues.value[index];
  if (!target) return;
  target.value = value;
  editedBusinessFreeColumnNos.value.add(target.columnNo);
  isEdited.value = true;
};

/** 変更された自由欄を含む関連取引一括編集patchを作成する */
const createBaseBusinessPatchWithFreeColumns = (): Partial<IBusiness> => ({
  ...createBaseBusinessPatch(),
  businessFreeColumnValues: businessFreeColumnValues.value
    .filter((item) => editedBusinessFreeColumnNos.value.has(item.columnNo))
    .map((item) => ({ ...item })),
});

/**
 * 自由入力カラムの編集状態を変更する
 */
const markBusinessFreeColumnEdited = () => {
  isEdited.value = true;
};

initializeBusinessFreeColumns();

defineExpose({
  fieldState,
  nullableFormState,
  selectedUserAccounts,
  businessFreeColumnValues,
  allValidate,
  isEdited,
  createBaseBusinessPatch: createBaseBusinessPatchWithFreeColumns,
  initializeForm,
});
</script>
<template>
  <div class="update-base-section">
    <div class="update-base-section__container">
      <template v-for="(option, index) in formOptionsBusiness" :key="option">
        <div class="item">
          <template v-if="inputTypeKeyMap[option] === 'selectUser'">
            <MultipleUserSelectInput
              :select-input-index="index"
              label=""
              :placeholder="nullableFormState[option] ? t(UNASSIGNED_COLUMN) : t(option)"
              :original-selected-user-id-list="fieldState[option].value.value ? fieldState[option].value.value.split(',') : []"
              :disabled="nullableFormState[option]"
              @update:model-value-user-accounts="updateUserAccountsValue"
              @update:model-value="(e) => updateUserValue(option, e)"
            />
          </template>
          <template v-else>
            <SingleSelectInput
              :index="index"
              label=""
              :model-value="nullableFormState[option] ? t(UNASSIGNED_COLUMN) : fieldState[option].value.value"
              :placeholder="t(option)"
              :filter-type="keyMappingForSelectInput[option]"
              :is-error="fieldState[option]?.isError.value"
              :error-message="fieldState[option]?.errorMessage.value"
              :disabled="nullableFormState[option]"
              disable-unassigned-column
              @click:select-input="() => (currentOpenSelectInputName = option)"
              @update:model-value="(e) => updateValue(option, e)"
            />
          </template>
          <div v-if="(BaseBusinessNullableFormItem as readonly string[]).includes(option)" class="item--checkbox">
            <label :for="`cb-${option}`">{{ t('この項目を空白にする') }}</label>
            <TableCheckbox
              :input-id="`cb-${option}`"
              :is-checked="nullableFormState[option]"
              :size="14"
              @change="(isChecked) => isChangedNullableCheckbox(option, isChecked)"
            />
          </div>
        </div>
      </template>
      <template v-for="(freeColumn, index) in businessFreeColumnValues" :key="freeColumn.columnNo">
        <div class="item">
          <MultiTagInput
            v-if="freeColumn.isMulti"
            :model-value="freeColumn.value"
            label=""
            :placeholder="freeColumn.headerName"
            @editing="markBusinessFreeColumnEdited"
            @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
          />
          <TextInput
            v-else
            :model-value="freeColumn.value"
            label=""
            :placeholder="freeColumn.headerName"
            ensure-error-msg-height
            error-message-position="bottom"
            @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
          />
        </div>
      </template>
    </div>
    <AddOptionWideSection :is-aggregate-business="false" @set-option="addNewOption" />
  </div>
</template>
<style lang="scss" scoped>
.update-base-section {
  display: grid;
  gap: 12px;
  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;

    grid-template-columns: repeat(6, 1fr);
  }
}
.item {
  display: grid;
  grid-template-columns: 100%;
  height: fit-content;

  box-sizing: border-box;
  border-bottom: solid 1px $borderDefault;
  &--checkbox {
    padding: 4px 4px 0 0;
    display: grid;
    grid-template-columns: 1fr max-content;
    font-size: 12px;
    justify-content: space-between;
    align-items: center;
    user-select: none;
  }
}
</style>
